import React, { FC, createContext, useContext, ReactNode, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { useLogger } from '~Hooks';
import { EPOS_CLOUD_CONFIG_QUERY } from './api/queries';
import {
  EposCloudConfig,
  EposCloudConfig_operativeViewer_eposCloudConfig,
} from './api/__queries__';

/** Shorthand for the generated config shape. */
type CloudConfig = EposCloudConfig_operativeViewer_eposCloudConfig;

/** Every field is undefined until the cloud config has loaded (or when the venue has none). */
interface EposThemeContextType {
  main: CloudConfig['main'] | undefined;
  secondary: CloudConfig['secondary'] | undefined;
  contrastingColor: CloudConfig['contrastingColor'] | undefined;
  menuTextColor: CloudConfig['menuTextColor'] | undefined;
  priceColor: CloudConfig['priceColor'] | undefined;
  menuBackgroundColor: CloudConfig['menuBackgroundColor'] | undefined;
  /** Set -> Welcome offers only that one service type, as a single "Order" button. */
  limitedToServiceType: CloudConfig['limitedToServiceType'] | undefined;
  /** The receipt question is skipped and assumed "yes" when this is set. */
  onlyPrintReceipt: CloudConfig['onlyPrintReceipt'] | undefined;
  /** Shown as the subtitle on DoneStep instead of the generic copy, translated with i18n. */
  exitMessage: CloudConfig['exitMessage'] | undefined;
  /** Printed on the order-number ticket, translated with i18n - see OrderIdMaster. */
  orderNumberPrintMessage: CloudConfig['orderNumberPrintMessage'] | undefined;
  /** Photo behind the whole kiosk flow (styles/Stage.tsx). Unset -> the stage says so instead. */
  backgroundMediaSrcUrl: CloudConfig['backgroundMediaSrcUrl'] | undefined;
  /** Rendered as a QR code on the order-number slip; no QR section is printed when unset. */
  qrCodeLink: CloudConfig['qrCodeLink'] | undefined;
  /** Venue icon/logo shown top-center on the Welcome scene; nothing is rendered when unset. */
  iconImageSrcUrl: CloudConfig['iconImageSrcUrl'] | undefined;
  /** Item detail shows one modification group per page instead of a single scrolling list. */
  oneModificationGroupPerPage: CloudConfig['oneModificationGroupPerPage'] | undefined;
  loading: boolean;
}

const EposThemeContext = createContext<EposThemeContextType | undefined>(undefined);

export const useEposTheme = (): EposThemeContextType => {
  const context = useContext(EposThemeContext);
  if (!context) {
    throw new Error('useEposTheme must be used within an EposThemeProvider');
  }
  return context;
};

export const EposThemeProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const logger = useLogger('epos-theme-provider');

  const eposCloudConfigQuery = useQuery<EposCloudConfig>(EPOS_CLOUD_CONFIG_QUERY, {
    fetchPolicy: 'cache-and-network',
    onError: (error) => {
      logger.error('Failed to fetch EPOS cloud config', { errorMessage: error.message });
    },
  });

  const contextValue = useMemo<EposThemeContextType>(() => {
    const config = eposCloudConfigQuery.data?.operativeViewer?.eposCloudConfig;
    return {
      main: config?.main,
      secondary: config?.secondary,
      contrastingColor: config?.contrastingColor,
      menuTextColor: config?.menuTextColor,
      priceColor: config?.priceColor,
      menuBackgroundColor: config?.menuBackgroundColor,
      limitedToServiceType: config?.limitedToServiceType,
      onlyPrintReceipt: config?.onlyPrintReceipt,
      exitMessage: config?.exitMessage,
      orderNumberPrintMessage: config?.orderNumberPrintMessage,
      backgroundMediaSrcUrl: config?.backgroundMediaSrcUrl,
      qrCodeLink: config?.qrCodeLink,
      iconImageSrcUrl: config?.iconImageSrcUrl,
      oneModificationGroupPerPage: config?.oneModificationGroupPerPage,
      loading: eposCloudConfigQuery.loading,
    };
  }, [eposCloudConfigQuery.data, eposCloudConfigQuery.loading]);

  return <EposThemeContext.Provider value={contextValue}>{children}</EposThemeContext.Provider>;
};

export default EposThemeProvider;
